<template>
	<v-card class="forum-card elevation-0 d-flex flex-column">
		<div class="forum-card__container">
			<div class="forum-card__outer">
				<div class="d-flex forum-card__list px-0">
					<div
						v-if="forum && forum.user"
						class="d-flex justify-space-between align-center pa-3"
						style="width: 100%"
					>
						<div @click="goToUserProfile" class="d-flex align-center cursor-pointer">
							<Avatar
								class="mr-2 category-card__avatar"
								:size="50"
								:avatar="forum.user.user_logo"
							/>
							<div class="forum-card__meta">
								<span class="forum-card__meta-top">
									{{ forum.user.visible_username }}
								</span>
								<span class="forum-card__meta-bottom">
                  {{forum.user.second_profile_row }}
                </span>
							</div>
						</div>
						<div class="forum-card__meta-time">
							<div class="d-flex flex-column align-end" style="height: 100%">
								<button
									@click="openModal"
									v-if="isOwnCurrentUserComment"
									class="forum-card__meta-time__button mb-1"
									:class="{
										'forum-card__meta-time__button--active': isEditModal,
									}"
                  type="button"
									style="align-self: flex-end"
								>
									<Icon type="ellipsis" :size="24" color="var(--accent-color)" />
								</button>
								<div
									v-if="isEditModal"
									class="forum-card__meta-time__button__modal"
									v-click-outside="closeModal"
								>
									<Icon type="pencil" :size="24" color="var(--accent-color)" />
									<button @click="openEditModal" type="button">Edit</button>
								</div>
								<div style="margin-top: auto">
									{{ dateCreated }}
								</div>
							</div>
						</div>
					</div>
					<v-divider />

					<div
						v-if="this.forum && this.forum.forum_text"
						class="forum-card__forum-text"
						v-html="this.postDescription(this.forum.forum_text)"
					></div>
					<v-dialog
						v-if="mobileLink"
						v-model="mobileLink"
						max-width="588px"
						width="100%"
						persistent
					>
						<v-card class="exhibitor-page__modal">
							<div class="exhibitor-page__modal__title">
								This screen is not available in the Web App, but you can access it
								through the mobile app
							</div>
							<button
								type="button"
								@click="toggleCloseMobileLink"
								class="exhibitor-page__modal__btn"
							>
								OK
							</button>
						</v-card>
					</v-dialog>

					<div v-if="forum.uploads && forum.uploads.length">
						<router-link
							class="d-flex align-center px-3 mt-3 text-decoration-none"
							:to="{ name: 'forum-file-by-id', params: { id: forum.forum_id } }"
						>
							<div
								class="d-flex flex-row flex-wrap w-full"
								v-for="attachment in this.forum.uploads.slice(0, 6)"
								:key="attachment.upload_id"
							>
								<PostAttachments
									backgroundColor="#F3F3F6"
									:attachment="attachment"
								/>
							</div>
							<div
								v-if="forum.uploads.length > 6"
								class="more-attachments"
								style="cursor: pointer"
							>
								+{{ countAttachments }}
							</div>
						</router-link>
					</div>
				</div>

				<v-divider class="mt-3" />

				<div class="custom_container">
					<div class="d-flex">
						<div class="d-flex align-center mr-6">
							<Icon type="eye" color="#9b9b9b" :size="24" :pr="6" />
							<div class="forum-card__counter-num">
								{{ forum.views_count }}
							</div>
						</div>
						<div class="d-flex align-center">
							<Icon type="comment" color="#9b9b9b" :size="24" :pr="6" />
							<div class="forum-card__counter-num">
								{{ forum.comment_count }}
							</div>
						</div>
					</div>
					<div
						class="forum-card__follow"
						:style="{color: isFollowed ? '#9b9b9b' : 'var(--accent-color)'}"
						@click="onFollow"
					>
						{{ isFollowed ? 'Followed' : 'Follow' }}
					</div>
				</div>

				<div class="post-card__comments-view">
					<Comment
						v-for="item in normalizedComments"
						:key="item.id"
						:comment="item"
						:post="{
							thread_id: item.threadId,
							user: item.user,
						}"
					/>
				</div>
				<v-divider v-if="this.comments.length === 0" />
				<div v-if="!isCommentActive" class="d-flex align-center">
					<div style="width: 100%" v-if="this.isAuthenticated">
						<CommentForm :avatar="this.profile.user_logo" :parent="this.forum" />
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import Avatar from '@/components/Avatar'
import { forumFollow, forumUnfollow } from '@/api/forum'
import CommentForm from '@/components/Forms/CommentForm.vue'
import { mapActions, mapGetters } from 'vuex'
import { normalizeComment } from '@/utils/helpers'
import Comment from '@/components/Comment.vue'
import SessionAttachments from '@/components/Resource/SessionAttachments.vue'
import PostAttachments from '@/components/Post/PostAttachments.vue'
import linkifyThreadText from '@/mixins/linkifyThreadText'

Vue.use(VueScrollTo)

export default {
	name: 'ForumCard',
	components: {
		PostAttachments,
		CommentForm,
		Comment,
		Avatar,
		SessionAttachments,
	},
	mixins: [linkifyThreadText],
	data: () => ({
		mobileLink: false,
		isFollowed: false,
		isEditModal: false,
	}),
	props: {
		forum: {
			type: Object,
			default: () => ({}),
		},
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated', 'mapRegisterToken']),
		...mapGetters('forum', ['comments', 'isCommentActive']),
		...mapGetters('settings', ['appCore']),
		countAttachments() {
			if (Array.isArray(this.forum.uploads)) {
				return this.forum.uploads.length - 6
			}
			return 0
		},
		isOwnCurrentUserComment() {
			return this.forum.user.user_id === this.profile.user_id
		},
		dateCreated() {
			const date = new Date(this.forum.created * 1000)
			const options = { year: 'numeric', month: 'long', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},

		/**
		 * @returns Array
		 */
		normalizedComments: function () {
			return this.comments.map((item) => normalizeComment(item))
		},
	},
	methods: {
		...mapActions('auth', ['getMapRegisterToken']),
		toggleCloseMobileLink() {
			this.mobileLink = false
		},
		closeModal() {
			this.isEditModal = false
		},
		openModal() {
			this.isEditModal = !this.isEditModal
		},
		openEditModal() {
			this.$emit('toggleEditModal')
			this.isEditModal = false
		},
		goToUserProfile() {
			if (!this.forum || !this.forum.user) return

			const userId = this.forum.user.user_id

			if (this.isCurrentUser(userId)) {
				this.$router.push({ name: 'profile' })
			} else {
				this.$router.push({ name: 'user-by-id', params: { id: userId } })
			}
		},
		onFollow() {
			if (this.isFollowed) {
				forumUnfollow(this.forum.forum_id)
			} else if (!this.isFollowed) {
				forumFollow({ forum_id: this.forum.forum_id })
			}

			this.isFollowed = !this.isFollowed
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
	},
	created() {
		if (this.forum) {
			this.isFollowed = this.forum.follow
		}
	},
}
</script>

<style lang="scss">
.forum-card {
	background: #f1f1f1 !important;

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 8px !important;
		background: #ffffff !important;
		border: 1px solid #e2e2e2 !important;
		border-bottom: none !important;
	}
	&__forum-text {
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 16px;
		line-height: 140%;
		letter-spacing: 0;
		color: #4a4a4a;
		padding: 12px 12px 0 12px;
		overflow-wrap: break-word;
		hr {
			max-width: 40px;
			margin: 12px 0;
		}
		li {
			margin-left: 12px;
		}
		br {
			display: none;
		}
		a {
			color: var(--link) !important;
			text-decoration: none;
			cursor: pointer !important;
		}
	}

	&__counter {
		&-num {
			color: #9b9b9b;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
		}
	}

	&__follow {

		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		text-decoration: unset;
		cursor: pointer;
	}

	&__meta {
		min-height: 46px;
		display: flex;
    word-break: break-word;
		justify-content: space-between;
		flex-direction: column;

		&-bottom {
			color: var(--company-position-large);
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 15px;
			line-height: 20px;
			letter-spacing: 0;
		}

		&-top {
			color: var(--username-category-title);
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
		}

		&-time {
			height: 46px;
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			color: #9b9b9b;
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 13px;
			line-height: 20px;
			letter-spacing: 0;
			&__button--active {
				width: 24px;
				height: 24px;
				border: 1.5px solid #0055a3;
				box-shadow: 0 0 0 2px #0055a333;
				border-radius: 4px;
				display: flex;
				align-items: center;
				justify-content: center;
			}
			&__button {
				position: relative;
				width: 24px;
				height: 24px;
				&__modal {
					font-weight: 400;
					font-size: 14px;
					line-height: 100%;
					letter-spacing: 0;
					position: absolute;
					top: 42px;
					right: 12px;
					width: 250px;
					height: 72px;
					border-radius: 8px;
					background: #ffffff;
					color: var(--accent-color);
					border: 1px solid #e2e2e2;
					font-family: 'Inter', sans-serif;
					display: flex;
					gap: 8px;
					align-items: center;
					padding: 24px;
				}
			}
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__title {
		color: #4a4a4a !important;
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 400;
		letter-spacing: 0;
		line-height: 17.07px;
		font-style: normal;
		word-break: break-word;
		white-space: pre-wrap;
	}

	&__block-view {
		flex-direction: column;
		height: 73%;

		.post-preview-card__thumbnail-outer img {
			max-width: 100%;
		}
	}

	&__content {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 21px;
	}

	&__thumbnail {
		height: auto;
		width: 100%;
	}

	&__menu-dots {
		margin-left: 10px !important;
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
		}
		&__default-thumbnail {
			max-height: 250px;
			height: 100%;
		}
		&__outer {
			flex-direction: column;
		}
	}
}

.custom_container {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px;
}
</style>
